import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    Grid,
    useTheme,
    Avatar,
    IconButton,
    Zoom
} from '@mui/material';
import {
    Close as CloseIcon,
    Stars as XpIcon,
    MonetizationOn as CoinsIcon,
    TrendingUp as LevelIcon,
    EmojiEvents as AchievementIcon,
} from '@mui/icons-material';
import ConfettiExplosion from 'react-confetti-explosion';
import {Particles} from '@tsparticles/react';
import {loadFull} from 'tsparticles';
import Quantino from './Quantino/Quantino'; // Import Quantino

const RewardCard = ({ reward, delay = 0 }) => {
    const [visible, setVisible] = useState(false);
    const theme = useTheme();

    useEffect(() => {
        const timer = setTimeout(() => setVisible(true), delay);
        return () => clearTimeout(timer);
    }, [delay]);

    const getRewardConfig = (reward) => {
        switch (reward.type) {
            case 'xp':
                return {
                    icon: <XpIcon sx={{ fontSize: 40 }} />,
                    bgColor: `linear-gradient(135deg, ${theme.palette.warning.light}, ${theme.palette.warning.main})`,
                    title: `+${reward.amount} XP`,
                    subtitle: reward.description || 'Für deine Leistung!'
                };
            case 'coins':
                return {
                    icon: <CoinsIcon sx={{ fontSize: 40 }} />,
                    bgColor: `linear-gradient(135deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`,
                    title: `+${reward.amount} Coins`,
                    subtitle: reward.description || 'Für deinen Einsatz!'
                };
            case 'level_up':
                return {
                    icon: <LevelIcon sx={{ fontSize: 40 }} />,
                    bgColor: `linear-gradient(135deg, ${theme.palette.success.light}, ${theme.palette.success.main})`,
                    title: 'Level Up!',
                    subtitle: `Du bist jetzt Level ${reward.newLevel}!`
                };
            case 'achievement':
                return {
                    icon: <AchievementIcon sx={{ fontSize: 40 }} />,
                    bgColor: `linear-gradient(135deg, ${theme.palette.secondary.light}, ${theme.palette.secondary.main})`,
                    title: reward.title || 'Achievement Unlocked!',
                    subtitle: reward.description || 'You\'ve earned a new achievement!'
                };
            default:
                return { icon: null, bgColor: 'grey.800', title: 'Belohnung', subtitle: '' };
        }
    };

    const config = getRewardConfig(reward);

    return (
        <Zoom in={visible} timeout={500} style={{ transitionDelay: `${delay}ms` }}>
            <Card sx={{
                background: config.bgColor,
                color: 'white',
                minHeight: 140, // Slightly smaller
                height: '100%', // Fill available height
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
                boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                borderRadius: 3
            }}>
                <CardContent sx={{ py: 2, px: 1.5 }}>
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 50, height: 50, mb: 1.5, mx: 'auto' }}>
                        {config.icon}
                    </Avatar>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 0.5, fontSize: '1.3rem' }}>
                        {config.title}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.85rem', lineHeight: 1.3 }}>
                        {config.subtitle}
                    </Typography>
                </CardContent>
            </Card>
        </Zoom>
    );
};

const GamificationRewardsModal = ({ open, onClose, rewards, newLevel, newAchievements, levelUp }) => {
    const theme = useTheme();
    const [processedRewards, setProcessedRewards] = useState([]);
    const [particlesOptions, setParticlesOptions] = useState(null);
    const [quantinoState, setQuantinoState] = useState({ mood: 'feiernd', message: '' });

    // Dynamic Quantino mood and message based on performance context
    useEffect(() => {
        if (open && rewards) {
            const totalXp = rewards.xp_earned || 0;
            const hasLevelUp = levelUp || newLevel;
            const hasAchievements = newAchievements && newAchievements.length > 0;
            
            // Define mood arrays for different scenarios
            const excellentMoods = ['feiernd', 'stolz_daumen_hoch', 'überrascht'];
            const goodMoods = ['motivierend', 'freundlich', 'zwinkernd', 'stolz_daumen_hoch'];
            const encouragingMoods = ['motivierend', 'freundlich', 'nachdenklich'];
            const lowPerformanceMoods = ['streng', 'traurig', 'motivierend']; // Still motivational but acknowledging low performance
            
            let selectedMood, selectedMessage;
            
            // Level up gets highest priority - always exciting
            if (hasLevelUp) {
                selectedMood = excellentMoods[Math.floor(Math.random() * excellentMoods.length)];
                const levelMessages = [
                    `Level up! You're getting stronger every day!`,
                    `New level achieved! Your hard work is paying off!`,
                    `Level up! You're becoming a true learning champion!`,
                    `Amazing! You've reached a new level of mastery!`
                ];
                selectedMessage = levelMessages[Math.floor(Math.random() * levelMessages.length)];
            }
            // High XP rewards (good performance)
            else if (totalXp >= 15) {
                selectedMood = excellentMoods[Math.floor(Math.random() * excellentMoods.length)];
                const highXpMessages = [
                    `Wow! ${totalXp} XP! You're absolutely crushing it!`,
                    `Incredible! ${totalXp} XP shows real mastery!`,
                    `Outstanding! ${totalXp} XP - you're on fire!`,
                    `Amazing work! ${totalXp} XP is truly impressive!`
                ];
                selectedMessage = highXpMessages[Math.floor(Math.random() * highXpMessages.length)];
            }
            // Medium XP rewards (decent performance)
            else if (totalXp >= 8) {
                selectedMood = goodMoods[Math.floor(Math.random() * goodMoods.length)];
                const mediumXpMessages = [
                    `Great job! ${totalXp} XP shows solid progress!`,
                    `Well done! ${totalXp} XP is good work!`,
                    `Nice! ${totalXp} XP - you're building momentum!`,
                    `Good effort! ${totalXp} XP keeps you moving forward!`
                ];
                selectedMessage = mediumXpMessages[Math.floor(Math.random() * mediumXpMessages.length)];
            }
            // Low XP but still some reward (encouraging)
            else if (totalXp >= 3) {
                selectedMood = encouragingMoods[Math.floor(Math.random() * encouragingMoods.length)];
                const lowXpMessages = [
                    `Every ${totalXp} XP counts! You're still moving forward!`,
                    `${totalXp} XP earned! Small steps lead to big victories!`,
                    `Progress is progress! ${totalXp} XP shows you're trying!`,
                    `Keep going! ${totalXp} XP is better than zero!`
                ];
                selectedMessage = lowXpMessages[Math.floor(Math.random() * lowXpMessages.length)];
            }
            // Very low performance (constructive but acknowledging struggle)
            else {
                selectedMood = lowPerformanceMoods[Math.floor(Math.random() * lowPerformanceMoods.length)];
                const constructiveMessages = [
                    `Learning takes time - don't give up! Every attempt teaches us something!`,
                    `I see you're working hard! Sometimes the best lessons come from challenges!`,
                    `It's okay to struggle - that's how we grow! Keep pushing forward!`,
                    `Remember, every expert was once a beginner! You've got this!`
                ];
                selectedMessage = constructiveMessages[Math.floor(Math.random() * constructiveMessages.length)];
            }
            
            // Special case for achievements (override if present)
            if (hasAchievements) {
                selectedMood = excellentMoods[Math.floor(Math.random() * excellentMoods.length)];
                const achievementMessages = [
                    `New achievements unlocked! You're becoming legendary!`,
                    `Achievement earned! Your dedication is showing!`,
                    `Wow! New achievements! You're really mastering this!`,
                    `Incredible! Achievement unlocked - you should be proud!`
                ];
                selectedMessage = achievementMessages[Math.floor(Math.random() * achievementMessages.length)];
            }
            
            setQuantinoState({ mood: selectedMood, message: selectedMessage });
        }
    }, [open, rewards, newLevel, newAchievements, levelUp]);

    useEffect(() => {
        if (open) {
            const allRewards = [];

            if (rewards) {
                if (rewards.xp_earned > 0) {
                    allRewards.push({
                        type: 'xp',
                        amount: rewards.xp_earned,
                        description: 'Für deine Antworten!'
                    });
                }
                if (rewards.coins_earned > 0) {
                    allRewards.push({
                        type: 'coins',
                        amount: rewards.coins_earned,
                        description: 'Für deinen Einsatz!'
                    });
                }
            }

            if (newLevel) {
                allRewards.push({
                    type: 'level_up',
                    newLevel: newLevel,
                });
            }

            // Add achievements if present
            if (newAchievements && newAchievements.length > 0) {
                newAchievements.forEach(achievement => {
                    allRewards.push({
                        type: 'achievement',
                        achievement: achievement,
                        title: achievement.title || achievement.name || 'Achievement Unlocked!',
                        description: achievement.description || 'You\'ve earned a new achievement!'
                    });
                });
            }

            setProcessedRewards(allRewards);
        }
    }, [open, rewards, newLevel]);

    const showConfetti = open && newLevel; // Show confetti on level up

    return (
        <>
            {showConfetti && (
                <Box sx={{ position: 'fixed', top: '50%', left: '50%', zIndex: 9999 }}>
                    <ConfettiExplosion particleCount={250} force={0.8} duration={4000} />
                </Box>
            )}
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
                        color: 'white',
                        boxShadow: '0 16px 40px rgba(0,0,0,0.3)',
                        minHeight: '60vh', // Responsive minimum height
                        maxHeight: '85vh', // Responsive maximum height
                        overflow: 'hidden', // Prevent overflow issues
                        display: 'flex',
                        flexDirection: 'column'
                    }
                }}
            >
                <DialogTitle sx={{ 
                    textAlign: 'center', 
                    p: 2, 
                    pt: 3,
                    flexShrink: 0 // Prevent shrinking
                }}>
                    <Typography component="span" variant="h4" sx={{ fontWeight: 800, textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
                        Rewards!
                    </Typography>
                    <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8, color: 'white' }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ 
                    p: 0, // Remove default padding
                    position: 'relative',
                    overflow: 'auto', // Allow scrolling if needed
                    flexGrow: 1, // Take available space
                    display: 'flex',
                    flexDirection: 'column'
                }}>
                    {/* Speech bubble and Quantino section */}
                    <Box sx={{ 
                        flexShrink: 0, // Prevent shrinking
                        px: 3,
                        pt: 1,
                        pb: 2
                    }}>
                        {/* Speech bubble at the top, pointing down to Quantino */}
                        <Box sx={{ 
                            display: 'flex',
                            justifyContent: 'center',
                            mb: 1
                        }}>
                            <Box sx={{
                                backgroundColor: 'white',
                                borderRadius: 3,
                                padding: 2,
                                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                                position: 'relative',
                                maxWidth: '85%',
                                textAlign: 'center',
                                '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    bottom: -10, // Arrow at bottom pointing down
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    width: 0,
                                    height: 0,
                                    borderLeft: '10px solid transparent',
                                    borderRight: '10px solid transparent',
                                    borderTop: '10px solid white', // Points down
                                }
                            }}>
                                <Typography variant="body1" sx={{ fontWeight: 500, color: '#2c3e50' }}>
                                    {quantinoState.message}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Quantino centered below speech bubble */}
                        <Box sx={{ 
                            display: 'flex',
                            justifyContent: 'center',
                            mb: 2
                        }}>
                            <Box sx={{ transform: 'scale(1.05)' }}> {/* Slightly smaller to fit better */}
                                <Quantino 
                                    mood={quantinoState.mood}
                                    message="" // No message since we show it above
                                    isVisible={true} 
                                    isBubbleVisible={false} // Don't show built-in bubble
                                    inline={true} 
                                />
                            </Box>
                        </Box>
                    </Box>
                    
                    {/* Rewards section - flexible height */}
                    <Box sx={{ 
                        flexGrow: 1,
                        px: 3,
                        pb: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}> 
                        {processedRewards.length > 0 ? (
                            <Grid container spacing={2} sx={{ maxWidth: '100%' }}>
                                {processedRewards.map((reward, index) => (
                                    <Grid item xs={12} sm={processedRewards.length > 2 ? 6 : processedRewards.length > 1 ? 6 : 12} key={index}>
                                        <RewardCard reward={reward} delay={index * 200} />
                                    </Grid>
                                ))}
                            </Grid>
                        ) : null}
                    </Box>
                </DialogContent>

                <DialogActions sx={{ 
                    justifyContent: 'center', 
                    p: 3, 
                    pt: 2,
                    flexShrink: 0 // Prevent shrinking
                }}>
                    <Button onClick={onClose} variant="contained" size="large" color="secondary">
                        Check Results
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export { GamificationRewardsModal };